/**
 * Search V2
 */

import _uniqBy from "lodash/uniqBy";
import _sortBy from "lodash/sortBy";
import * as actionTypes from "../actions/actionTypes";
import constructKeywordFilter from "../helpers/constructKeywordFilter";

export const searchV2Steps = {
  INPUT: "INPUT",
  RESULTS: "RESULTS",
};

const DEFAULT_SORT = "relevance";
const DEFAULT_STATE = {
  step: searchV2Steps.INPUT,
  suggestions: {},
  kitSuggestions: { results: [] },
  filters: [],
  query: null,
  sort: DEFAULT_SORT,
  prefixes: [],
  results: {
    items: { results: [] },
    jumpTo: { results: [] },
  },
  searchHistory: JSON.parse(window.localStorage.getItem("search_history")) || [],
};

function pushSearchHistory(state, filters) {
  let { searchHistory } = state;
  if (filters.length > 0) {
    const newHistory = [...state.searchHistory];
    newHistory.unshift(_sortBy(filters, [f => f.type === "keyword", f => f.type]));
    searchHistory = _uniqBy(
      newHistory,
      recent =>
        recent.find(f => f.type === "keyword")?.value ||
        recent
          .map(f => JSON.stringify(f.id + f.display))
          .sort()
          .join(",")
    ).slice(0, 3);
    window.localStorage.setItem("search_history", JSON.stringify(searchHistory));
  }
  return searchHistory;
}

// eslint-disable-next-line complexity
export default function searchV2(state = DEFAULT_STATE, action) {
  switch (action.type) {
    case actionTypes.TOGGLE_SEARCH_STEP:
      return { ...state, step: action.step };
    case actionTypes.ADD_QUERY_FILTER: {
      const existingFiltersWithoutDuplicate = action.filter.id
        ? state.filters.filter(f => f.id !== action.filter.id)
        : state.filters;
      return { ...state, filters: [...existingFiltersWithoutDuplicate, action.filter] };
    }
    case actionTypes.REPLACE_QUERY_FILTERS: {
      const sort = action.filters.length ? {} : { sort: DEFAULT_SORT };
      return { ...state, filters: action.filters, ...sort };
    }
    case actionTypes.CLEAR_QUERY_FILTERS:
      return { ...state, filters: [], sort: DEFAULT_SORT };
    case actionTypes.REMOVE_QUERY_FILTER: {
      const newFilters = action.filterId
        ? state.filters.filter(f => f.id !== action.filterId)
        : state.filters.slice(0, -1);
      const sort = newFilters.length ? {} : { sort: DEFAULT_SORT };
      return { ...state, filters: newFilters, suggestions: {}, ...sort };
    }
    case actionTypes.CHANGE_KEYWORD_FILTER: {
      const tokenFilters = state.filters.filter(f => f.type !== "keyword"),
        newKeywordFilter = constructKeywordFilter(action.keyword),
        newFilters = action.keyword ? [...tokenFilters, newKeywordFilter] : tokenFilters;
      const sort = newFilters.length ? {} : { sort: DEFAULT_SORT };
      return { ...state, filters: newFilters, ...sort };
    }
    case actionTypes.CHANGE_SEARCH_SORT:
      return { ...state, sort: action.sort };
    case actionTypes.CLEAR_RECENT_SEARCHES:
      window.localStorage.removeItem("search_history");
      return { ...state, searchHistory: [] };
    case actionTypes.FETCH_SEARCH_META_SUCCESS: {
      const prefixes = action.response.filters.reduce((acc, cur) => {
        if (cur.prefix) acc.push(cur.prefix);
        return acc;
      }, []);
      return { ...state, prefixes };
    }
    case actionTypes.FETCH_SEARCH_SUGGESTIONS_SUCCESS: {
      const currentId = state.suggestions.requestId ?? -1,
        { response, requestId } = action;
      if (!requestId || currentId > requestId) return state;
      return { ...state, suggestions: { requestId, ...response } || {} };
    }

    case actionTypes.SUBMIT_SEARCH_QUERY: {
      const { queries } = action.query;
      const results = { ...state.results };
      Object.keys(queries).forEach(k => {
        results[k] = {
          ...results[k],
          ...(action.page === 0
            ? {
                results: [],
                error: null,
                total: undefined,
              }
            : {}),
        };
      });
      return { ...state, results, step: searchV2Steps.RESULTS };
    }
    case actionTypes.SUBMIT_SEARCH_QUERY_SUCCESS: {
      const searchHistory = pushSearchHistory(state, action.query.filters);
      const {
        requestId,
        response: {
          result: { queries },
        },
      } = action;
      const results = { ...state.results };
      const currentId = results.requestId ?? -1;
      if (!requestId || currentId > requestId) return state;
      results.requestId = requestId;

      Object.keys(queries).forEach(k => {
        results[k] = {
          ...results[k],
          ...queries[k],
          error: null,
          ...(results[k].results && queries[k].results
            ? { results: [...results[k].results, ...queries[k].results] }
            : {}),
        };
      });
      return {
        ...state,
        step: searchV2Steps.RESULTS,
        results,
        searchHistory,
      };
    }
    case actionTypes.SUBMIT_SEARCH_QUERY_FAILURE: {
      const {
        query: { queries },
        error,
      } = action;
      const results = { ...state.results };
      Object.keys(queries).forEach(k => {
        results[k] = { error: error };
      });
      return { ...state, results, step: searchV2Steps.RESULTS };
    }
    default:
      return state;
  }
}
